// Here i have not deleted the comments beacuse they need in future to add tabs for settings pane.

import { Box, Tab, Theme, styled, Tabs, useTheme, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CoachPageLayout from '../../components/CoachPageLayout/CoachPageLayout';
import PasswordChange from '../../components/PasswordChange/PasswordChange';
import UpdatePersonalInfo from '../../components/Settings/UpdatePersonalInfo/UpdatePersonalInfo';
import AppSettings from '../../components/Settings/AppSettings/AppSettings';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/rootReducer';
import { fetchCoachSettings } from '../../redux/coach/coachSlice';
import PrivacySettings from '../../components/Settings/AppSettings/PrivacySettings';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import { UserStatusType } from '../../types/coach';
import i18n from '../../i18n';

const StyledBox = styled(Box)({
  backgroundColor: '#FFF',
  borderRadius: '10px',
  marginTop: 24,
  boxShadow: '0px 4px 40px 0px #FFDDC840',
});
const IconWrapper = styled(Box)`
  width: 16px;
  height: 16px;
  display: flex;
  margin-top: 2px;
  justify-content: center;
  align-items: center;
`;

interface IStyledProps {
  theme?: Theme;
}

export const SingleTab = styled(Tab)<IStyledProps>(({ theme }) => ({
  height: '52px',
  minHeight: '52px',
  display: 'flex',
  gap: '12px',
  justifyContent: 'center',
  flexDirection: 'row',
  alignItems: 'center',
  fontFamily: 'Satoshi',
  fontSize: '17px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '22px',
  color: theme.palette.primary.main,
  '&.Mui-selected': {
    fontWeight: 700,
    transition: 'font-weight 0.2s ease-in-out',
    background:
      'linear-gradient(138deg, rgba(101, 153, 165, 0.15) 24.11%, rgba(59, 102, 126, 0.15) 83.2%), #FFF',
    '& span': {
      background: 'linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
  },

  '.Mui-selected & svg': {
    stroke: 'black',
    strokeWidth: '4.5',
    fill: 'currentColor',
  },

  span: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    '@media (max-width: 770px)': {
      fontSize: '14px',
      maxWidth: '60px',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
  },
}));

function tabPanelProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}
interface ITabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}
const CustomTabPanel = ({ children, value, index, ...other }: ITabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
    style={{
      height: '100%',
      width: '100%',
    }}
  >
    {value === index && <Box height="100%">{children}</Box>}
  </div>
);

function useURLQuery() {
  return new URLSearchParams(useLocation().search);
}

export interface ITabProperty {
  label: string;
  icon: string;
  boldIcon: string;
  component: React.ReactNode;
  permissions?: string[];
}

const Settings = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const tab = useURLQuery().get('tab');
  const [validTabs, setValidTabs] = useState<string[]>([]);
  const [value, setValue] = useState(-2);
  const { t } = useTranslation();
  const [filteredTabProperties, setFilteredTabProperties] = useState<ITabProperty[]>([]);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const userStatusType = useAppSelector((state) => state.coach.coach?.userStatusType);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const tabsProperties = [
    {
      label: 'settings.tabs.personalInfo',
      icon: '/assets/icons/user.svg',
      boldIcon: '/assets/icons/user-bold.svg',
      component: (
        <StyledBox width={isSmallScreen ? 500 : 889} maxHeight={596} gap="36px">
          <UpdatePersonalInfo />
        </StyledBox>
      ),
      tab: 'personal-info',
    },
    {
      label: 'settings.tabs.password',
      icon: '/assets/icons/key.svg',
      boldIcon: '/assets/icons/key-bold.svg',
      component: (
        <StyledBox width={isSmallScreen ? 600 : 766} maxHeight={386} p={4} gap="36px">
          <PasswordChange />
        </StyledBox>
      ),
      tab: 'forget-password',
    },
    {
      label: 'settings.tabs.appSettings',
      icon: '/assets/icons/tuning-square.svg',
      boldIcon: '/assets/icons/tuning-square-bold.svg',
      component: (
        <StyledBox width={384} maxHeight={470} p={4}>
          <AppSettings />
        </StyledBox>
      ),
      permissions: [UserPermissionType.CORE_MODULE_VIEW_APP_SETTINGS],
      tab: 'app-settings',
    },
    {
      label: 'settings.tabs.privacySettings',
      icon: '/assets/icons/shield-user.svg',
      boldIcon: '/assets/icons/shield-user-bold.svg',
      component: <PrivacySettings />,
      permissions: [
        UserPermissionType.CORE_MODULE_COACH_DATA_REQUEST,
        UserPermissionType.CORE_MODULE_DELETE_COACH_ACCOUNT,
      ],
      tab: 'privacy-settings',
    },
  ];

  useEffect(() => {
    const filteredTabs = tabsProperties.filter((tabProperty) =>
      checkPermissions(tabProperty.permissions, false)
    );
    if (userStatusType === UserStatusType.EXTERNAL_PROVIDER) {
      const tabsForSSOUsers = filteredTabs.filter((item) => item.tab !== 'forget-password');
      setFilteredTabProperties(tabsForSSOUsers);
    } else {
      setFilteredTabProperties(filteredTabs);
    }
    setValidTabs(filteredTabs.map((singleTab) => singleTab.tab));
  }, [tabsProperties]);

  useEffect(() => {
    const newValue = validTabs.indexOf(tab || validTabs[0]);
    setValue(newValue !== -1 ? newValue : 0);
  }, [value, id, navigate]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    const tabName = validTabs[newValue];
    navigate(`/settings?tab=${tabName}`);
  };

  const dispatch = useDispatch();
  const coach = useAppSelector((state: RootState) => state.coach.coach);
  useEffect(() => {
    if (coach && checkPermissions(UserPermissionType.CORE_MODULE_VIEW_APP_SETTINGS)) {
      dispatch(fetchCoachSettings(coach.id));
    }
  }, [coach]);

  return (
    <CoachPageLayout>
      <Box width="100%" height="100%" pt={3}>
        <Box
          bgcolor="#fff !important"
          borderRadius="10px"
          justifyContent="center"
          boxShadow="0px 4px 40px 0px rgba(255, 221, 200, 0.25)"
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
            centered
            variant="fullWidth"
            sx={{
              width: '100%',
              height: '52px',
              borderRadius: '10px',
              border: '0.5px solid #7CAEBE',
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            {filteredTabProperties.map(
              (
                singleTab: {
                  label: string;
                  icon: string;
                  boldIcon: string;
                  permissions?: string[];
                },
                index
              ) => (
                <SingleTab
                  key={singleTab.label}
                  label={<span>{t(singleTab.label)}</span>}
                  icon={
                    <IconWrapper>
                      <img
                        src={value === index ? singleTab.boldIcon : singleTab.icon}
                        alt={`${singleTab.label}-icon`}
                      />
                    </IconWrapper>
                  }
                  sx={{
                    borderLeft:
                      i18n.language === 'ar' && index < tabsProperties.length - 1
                        ? '0.5px solid #7CAEBE'
                        : 'none',
                    borderRight:
                      i18n.language !== 'ar' && index < tabsProperties.length - 1
                        ? '0.5px solid #7CAEBE'
                        : 'none',
                  }}
                  {...tabPanelProps(index)}
                />
              )
            )}
          </Tabs>
        </Box>
        <Box height="90%">
          {filteredTabProperties.map((tabName, index) => (
            <CustomTabPanel key={tabName.label} value={value} index={index}>
              {tabName.component}
            </CustomTabPanel>
          ))}
        </Box>
      </Box>
    </CoachPageLayout>
  );
};

export default Settings;
